import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import InternalLinks from '../components/InternalLinks';
import { 
  CheckCircle, 
  ArrowRight, 
  Building2,
  FileText,
  AlertTriangle,
  Clock,
  Target,
  Award,
  Users,
  Euro,
  BookOpen,
  Lightbulb,
  XCircle,
  Menu,
  X
} from 'lucide-react';

export default function RepondreMarchesPublicsPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <>
      <SEO 
        title="Comment Répondre aux Marchés Publics | Guide Complet 2026"
        description="Guide complet pour répondre aux appels d'offres publics : mémoire technique, dossier de candidature, erreurs à éviter. Conseils d'experts pour PME."
        path="/repondre-marches-publics"
      />
      <div className="min-h-screen bg-white">
        {/* Header */}
        <header className="bg-gradient-to-r from-violet-900 via-indigo-900 to-slate-900 text-white py-4">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6" />
                </div>
                <span className="font-bold text-xl">Mon DCE Express</span>
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/veille-marches-publics" className="text-white/80 hover:text-white">Veille</Link>
                <Link to="/trouver-appels-offres" className="text-white/80 hover:text-white">Trouver</Link>
                <Link to="/analyse-dce" className="text-white/80 hover:text-white">Analyse DCE</Link>
                <Link to="/plateforme-appels-offres" className="text-white/80 hover:text-white">Plateforme</Link>
                <Link to="/blog" className="text-white/80 hover:text-white">Blog</Link>
                <Link to="/register">
                  <Button className="bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
                </Link>
              </div>
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-white/10"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
            {mobileMenuOpen && (
              <div className="md:hidden mt-4 pt-4 border-t border-white/20">
                <div className="flex flex-col gap-2">
                  <Link to="/" className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Accueil</Link>
                  <Link to="/veille-marches-publics" className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Veille marchés publics</Link>
                  <Link to="/trouver-appels-offres" className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Trouver appels d'offres</Link>
                  <Link to="/analyse-dce" className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Analyse DCE</Link>
                  <Link to="/plateforme-appels-offres" className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Plateforme</Link>
                  <Link to="/blog" className="px-4 py-3 text-white/80 hover:bg-white/10 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Blog</Link>
                  <div className="border-t border-white/20 mt-2 pt-4 flex flex-col gap-2">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10">Connexion</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full bg-white text-violet-900 hover:bg-slate-100">S'inscrire</Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-violet-900 via-indigo-900 to-slate-900 text-white py-20">
          <div className="container mx-auto px-4">
            <Breadcrumb items={[{ label: "Répondre aux marchés publics", href: "/repondre-marches-publics" }]} />
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm mb-6">
                <BookOpen className="w-4 h-4" />
                <span>Guide complet 2026</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Comment Répondre aux
                <br />
                <span className="text-violet-300">Marchés Publics</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
                Guide complet pour rédiger votre mémoire technique, constituer votre dossier de candidature et maximiser vos chances de remporter des appels d'offres.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                    Commencer gratuitement
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">
                Pourquoi répondre aux marchés publics ?
              </h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p>
                  Les marchés publics représentent une <strong>opportunité considérable</strong> pour les entreprises de toutes tailles. Avec plus de 200 milliards d'euros de commandes annuelles en France, la commande publique est un vivier de contrats stables et récurrents.
                </p>
                <p>
                  Contrairement aux idées reçues, <strong>60% des marchés publics sont attribués à des PME</strong>. Les acheteurs publics sont même encouragés à faciliter l'accès des petites structures via l'allotissement obligatoire et les avances de paiement.
                </p>
                <p>
                  Cependant, répondre à un appel d'offres demande de la méthode. Un dossier incomplet, une offre mal rédigée ou un prix mal calibré peuvent conduire à l'élimination. Ce guide vous accompagne pas à pas pour <strong>maximiser vos chances de succès</strong>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Les étapes clés */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Les 5 étapes pour répondre à un appel d'offres
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {[
                  {
                    step: "1",
                    title: "Analyser le DCE (Dossier de Consultation)",
                    desc: "Lisez attentivement le règlement de consultation (RC), le CCAP et le CCTP. Identifiez les critères d'attribution, leurs pondérations, et les documents obligatoires. Une lecture minutieuse évite les erreurs éliminatoires.",
                    link: { text: "Comment analyser un DCE rapidement →", href: "/analyse-dce" }
                  },
                  {
                    step: "2",
                    title: "Vérifier votre éligibilité",
                    desc: "Assurez-vous de répondre aux critères de sélection : capacités financières, références similaires, certifications requises. Vérifiez que les délais d'exécution sont compatibles avec votre charge de travail."
                  },
                  {
                    step: "3",
                    title: "Constituer le dossier de candidature",
                    desc: "Rassemblez les documents administratifs : DC1, DC2 (ou DUME), attestations fiscales et sociales, Kbis, références clients. Un dossier incomplet est éliminatoire, même si votre offre technique est excellente."
                  },
                  {
                    step: "4",
                    title: "Rédiger le mémoire technique",
                    desc: "C'est le cœur de votre offre. Démontrez votre compréhension du besoin, présentez votre méthodologie, détaillez vos moyens humains et matériels. Personnalisez-le pour chaque marché."
                  },
                  {
                    step: "5",
                    title: "Établir l'offre financière",
                    desc: "Remplissez le bordereau des prix avec attention. Votre prix doit être compétitif mais réaliste. Un prix anormalement bas peut conduire au rejet de votre offre."
                  }
                ].map((etape, i) => (
                  <div key={`item-${i}`} className="flex gap-6 items-start">
                    <div className="w-14 h-14 bg-violet-600 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">
                      {etape.step}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{etape.title}</h3>
                      <p className="text-slate-600 mb-2">{etape.desc}</p>
                      {etape.link && (
                        <Link to={etape.link.href} className="text-violet-600 hover:text-violet-700 font-medium">
                          {etape.link.text}
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Le mémoire technique */}
        <section className="py-16 bg-violet-900 text-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">
              Comment rédiger un mémoire technique convaincant
            </h2>
            <div className="max-w-4xl mx-auto">
              <div className="prose prose-lg prose-invert max-w-none mb-8">
                <p>
                  Le mémoire technique représente souvent <strong>40 à 60% de la note finale</strong>. C'est votre opportunité de vous démarquer et de prouver votre expertise. Voici la structure recommandée :
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    title: "1. Présentation de l'entreprise",
                    items: ["Historique et valeurs", "Chiffres clés", "Certifications et labels", "Moyens humains et matériels"]
                  },
                  {
                    title: "2. Compréhension du besoin",
                    items: ["Reformulation du contexte", "Enjeux identifiés", "Contraintes comprises", "Points d'attention"]
                  },
                  {
                    title: "3. Méthodologie proposée",
                    items: ["Phases d'exécution", "Planning détaillé", "Points de contrôle", "Livrables intermédiaires"]
                  },
                  {
                    title: "4. Moyens dédiés",
                    items: ["Équipe affectée (CV)", "Matériel mobilisé", "Sous-traitants éventuels", "Organisation du projet"]
                  },
                  {
                    title: "5. Références similaires",
                    items: ["Projets comparables", "Montants et dates", "Attestations clients", "Enseignements tirés"]
                  },
                  {
                    title: "6. Engagements qualité",
                    items: ["Indicateurs de performance", "Procédures de contrôle", "Gestion des anomalies", "Démarche RSE"]
                  }
                ].map((section, i) => (
                  <Card key={`item-${i}`} className="bg-white/10 border-white/20">
                    <CardContent className="p-6">
                      <h3 className="text-lg font-semibold mb-3">{section.title}</h3>
                      <ul className="space-y-2">
                        {section.items.map((item, j) => (
                          <li key={j} className="flex items-center gap-2 text-slate-200">
                            <CheckCircle className="w-4 h-4 text-violet-300 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Les erreurs à éviter */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Les 10 erreurs qui font perdre les marchés
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {[
                {
                  title: "Dossier incomplet",
                  desc: "Oublier une pièce du dossier de candidature entraîne l'élimination automatique, même si votre offre technique est parfaite."
                },
                {
                  title: "Non-respect des délais",
                  desc: "Une offre déposée après la date limite est irrecevable. Prévoyez une marge de sécurité pour les problèmes techniques."
                },
                {
                  title: "Mémoire technique générique",
                  desc: "Copier-coller un mémoire sans l'adapter au marché spécifique montre un manque d'investissement et de compréhension."
                },
                {
                  title: "Prix anormalement bas",
                  desc: "Un prix trop bas éveille la suspicion de l'acheteur et peut conduire au rejet. Justifiez toujours votre offre financière."
                },
                {
                  title: "Ignorer les critères de notation",
                  desc: "Chaque critère a une pondération. Concentrez vos efforts sur les critères qui pèsent le plus dans la notation."
                },
                {
                  title: "Négliger la présentation",
                  desc: "Un document mal mis en page, avec des fautes d'orthographe, donne une mauvaise image de votre professionnalisme."
                },
                {
                  title: "Oublier les variantes",
                  desc: "Si les variantes sont autorisées, proposez une alternative innovante. C'est une opportunité de vous différencier."
                },
                {
                  title: "Sous-estimer la concurrence",
                  desc: "Renseignez-vous sur les attributaires précédents. Analysez leurs points forts pour adapter votre stratégie."
                },
                {
                  title: "Ignorer les questions",
                  desc: "Posez vos questions à l'acheteur via la plateforme. Les réponses sont souvent partagées avec tous les candidats."
                },
                {
                  title: "Abandonner trop vite",
                  desc: "Les premiers échecs sont normaux. Demandez le rapport d'analyse pour comprendre et vous améliorer."
                }
              ].map((erreur, i) => (
                <Card key={`item-${i}`} className="border-red-200 bg-red-50/30">
                  <CardContent className="p-6">
                    <div className="flex gap-4">
                      <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">{erreur.title}</h3>
                        <p className="text-slate-600">{erreur.desc}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Les documents obligatoires */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-6 text-center">
              Les documents du dossier de candidature
            </h2>
            <div className="max-w-4xl mx-auto prose prose-lg text-slate-600">
              <p>
                Voici la liste des documents généralement demandés dans un appel d'offres. Préparez-les en amont pour gagner du temps :
              </p>
              <h3>Documents administratifs</h3>
              <ul>
                <li><strong>DC1</strong> - Lettre de candidature (identification du candidat)</li>
                <li><strong>DC2</strong> - Déclaration du candidat (capacités et références)</li>
                <li><strong>DUME</strong> - Document Unique de Marché Européen (simplifie DC1+DC2)</li>
                <li><strong>Kbis</strong> - Extrait récent (moins de 3 mois)</li>
                <li><strong>Attestations fiscales</strong> - Liasse 3666 ou attestation en ligne</li>
                <li><strong>Attestation URSSAF</strong> - Régularité des cotisations sociales</li>
                <li><strong>Attestation d'assurance RC</strong> - Responsabilité civile professionnelle</li>
              </ul>
              <h3>Documents techniques</h3>
              <ul>
                <li><strong>Mémoire technique</strong> - Réponse aux exigences du CCTP</li>
                <li><strong>Références</strong> - Marchés similaires réalisés</li>
                <li><strong>CV des intervenants</strong> - Équipe affectée au projet</li>
                <li><strong>Certifications</strong> - Qualibat, ISO, RGE selon le secteur</li>
                <li><strong>Planning prévisionnel</strong> - Phases et jalons</li>
              </ul>
              <h3>Documents financiers</h3>
              <ul>
                <li><strong>Acte d'engagement</strong> - Signé par le représentant légal</li>
                <li><strong>BPU / DQE</strong> - Bordereau des prix rempli</li>
                <li><strong>Décomposition du prix</strong> - Si demandée</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Conseils pour PME */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
              Conseils spécifiques pour les PME
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  icon: Target,
                  title: "Ciblez les bons marchés",
                  desc: "Concentrez-vous sur les marchés allotis et les montants adaptés à votre capacité. Mieux vaut 5 réponses soignées que 20 bâclées."
                },
                {
                  icon: Users,
                  title: "Groupez-vous si nécessaire",
                  desc: "Pour les marchés dépassant vos capacités, constituez un groupement avec des entreprises complémentaires."
                },
                {
                  icon: Euro,
                  title: "Demandez des avances",
                  desc: "Les PME peuvent obtenir jusqu'à 60% d'avance sur les marchés publics. N'hésitez pas à le mentionner."
                },
                {
                  icon: Clock,
                  title: "Automatisez votre veille",
                  desc: "Utilisez des outils pour surveiller les publications et ne plus manquer d'opportunités pertinentes."
                },
                {
                  icon: Award,
                  title: "Valorisez vos atouts",
                  desc: "Proximité, réactivité, engagement du dirigeant... Les PME ont des avantages que les grands groupes n'ont pas."
                },
                {
                  icon: Lightbulb,
                  title: "Innovez dans vos réponses",
                  desc: "Proposez des variantes, des solutions alternatives. L'innovation est de plus en plus valorisée."
                }
              ].map((conseil, i) => (
                <Card key={`item-${i}`} className="border-slate-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <conseil.icon className="w-6 h-6 text-violet-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{conseil.title}</h3>
                    <p className="text-slate-600">{conseil.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Internal Links */}
        <InternalLinks currentPage="repondre" />

        {/* CTA */}
        <section className="py-16 bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Prêt à remporter vos premiers marchés ?
            </h2>
            <p className="text-xl text-violet-100 mb-8 max-w-2xl mx-auto">
              Notre plateforme vous aide à trouver les appels d'offres pertinents et à analyser les DCE en quelques secondes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-white text-violet-900 hover:bg-slate-100 rounded-full px-8">
                  Créer mon compte gratuit
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/analyse-dce">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8">
                  Découvrir l'analyse IA
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-400 py-12">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h4 className="font-semibold text-white mb-4">DCE Express</h4>
                <p className="text-sm">Votre guide pour répondre aux marchés publics.</p>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-4">Pages piliers</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/veille-marches-publics" className="hover:text-white">Veille marchés publics</Link></li>
                  <li><Link to="/trouver-appels-offres" className="hover:text-white">Trouver appels d'offres</Link></li>
                  <li><Link to="/analyse-dce" className="hover:text-white">Analyse DCE</Link></li>
                  <li><Link to="/plateforme-appels-offres" className="hover:text-white">Plateforme</Link></li>
                  <li><Link to="/repondre-marches-publics" className="hover:text-white">Répondre aux marchés</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-4">Ressources</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                  <li><Link to="/guide-appels-offres" className="hover:text-white">Guide complet</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-white mb-4">Légal</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link></li>
                  <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
              <p>© 2026 DCE Express - Tous droits réservés</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
